// ADR 8.9.26i §3 — close a popover disclosure without a second implementation
// of "closed". Moved out of `layout/Header.tsx` by ADR 16.9.26p §3: the
// walkthrough's index panel is its second caller, a feature importing from
// `layout/` is the wrong direction, and `ui/controls/` is where a second
// caller finds it.
//
// §1's `Disclosure` owns its own open state — there is no `open`/`onToggle`
// pair, which is what lets `persistKey` mean anything — so the two behaviours
// a popover needs and a section does not are driven through the contract
// 8.9.26i AC1 already asserts: read `aria-expanded`, and if it is set, click
// the trigger. The close *and* the focus return are then §1's, rather than a
// remount that would drop focus to `<body>` on the route change it is meant
// to handle.

import { useCallback, useEffect, type RefObject } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Dismiss the open disclosure under `scope` on a route change and on a
 * pointerdown outside it. Returns `dismiss` so a caller can close it itself —
 * a jump from the walkthrough's index is neither of the two, and the phones'
 * sheet dismisses on tap.
 */
export function useDismissMenu(scope: RefObject<HTMLElement>): () => void {
  const dismiss = useCallback(() => {
    const trigger = scope.current?.querySelector('button[aria-expanded="true"]')
    if (trigger instanceof HTMLButtonElement) trigger.click()
  }, [scope])

  const { pathname } = useLocation()
  useEffect(() => {
    // A no-op while the menu is closed, which is every route change but the
    // one where the reader followed a link out of it.
    dismiss()
  }, [pathname, dismiss])

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target
      if (target instanceof Node && scope.current?.contains(target) === true) return
      dismiss()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [dismiss, scope])

  return dismiss
}
